"use client";

/**
 * JobCard — single analysis job entry with inline title editing,
 * status badge, progress bar, and actions.
 */
import { useState, useRef } from "react";
import { useRouter } from "next/navigation";
import type { AnalysisJob } from "../types";

interface Props {
  job: AnalysisJob;
  onDelete: (jobId: string) => Promise<void>;
  onRename: (jobId: string, title: string) => Promise<void>;
}

function typeClass(type: string): string {
  const map: Record<string, string> = {
    Spend: "jt-spend",
    Inventory: "jt-inventory",
    Contracts: "jt-contracts",
    Logistics: "jt-logistics",
  };
  return map[type] ?? "jt-default";
}

function StatusBadge({ status }: { status: AnalysisJob["status"] }) {
  const styles: Record<AnalysisJob["status"], { bg: string; border: string; color: string }> = {
    completed: { bg: "rgba(34,197,94,0.08)", border: "rgba(34,197,94,0.2)", color: "#22c55e" },
    processing: { bg: "rgba(251,146,60,0.08)", border: "rgba(251,146,60,0.2)", color: "#f97316" },
    error: { bg: "rgba(239,68,68,0.08)", border: "rgba(239,68,68,0.2)", color: "#ef4444" },
  };
  const s = styles[status];
  const labels = { completed: "Completed", processing: "Processing", error: "Failed" };
  return (
    <span style={{
      padding: "2px 10px", borderRadius: 20, fontSize: 11,
      background: s.bg, border: `1px solid ${s.border}`, color: s.color,
    }}>
      {labels[status]}
    </span>
  );
}

export function JobCard({ job, onDelete, onRename }: Props) {
  const router = useRouter();
  const [editing, setEditing] = useState(false);
  const [editTitle, setEditTitle] = useState(job.title);
  const [saving, setSaving] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  function startEditing(e: React.MouseEvent) {
    e.stopPropagation();
    setEditTitle(job.title);
    setEditing(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  function cancelEditing() {
    setEditing(false);
    setEditTitle(job.title);
  }

  async function saveTitle(e: React.MouseEvent | React.KeyboardEvent) {
    e.stopPropagation();
    if (!editTitle.trim()) { cancelEditing(); return; }
    setSaving(true);
    try {
      await onRename(job.id, editTitle.trim());
      setEditing(false);
    } catch {
      alert("Failed to update title. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Enter") saveTitle(e);
    else if (e.key === "Escape") cancelEditing();
  }

  async function handleDelete(e: React.MouseEvent) {
    e.stopPropagation();
    if (!confirm("Delete this analysis? This cannot be undone.")) return;
    await onDelete(job.id);
  }

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => router.push(`/analyse/jobs/${job.id}`)}
      onKeyDown={(e) => e.key === "Enter" && router.push(`/analyse/jobs/${job.id}`)}
      className={`job-card ${typeClass(job.type)}`}
    >
      {/* Top row: icon + status badge */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{
          width: 44, height: 44, borderRadius: 10,
          background: "var(--card-icon-bg, rgba(99,102,241,0.14))",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: 18,
        }}>
          {typeEmoji(job.type)}
        </div>
        <StatusBadge status={job.status} />
      </div>

      {/* Title */}
      {editing ? (
        <div
          style={{ display: "flex", alignItems: "center", gap: 6 }}
          onClick={(e) => e.stopPropagation()}
        >
          <input
            ref={inputRef}
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={saving}
            style={{
              flex: 1, padding: "6px 10px", borderRadius: 8,
              border: "1px solid var(--indigo)",
              background: "var(--paper-2)", fontSize: 15, fontWeight: 600,
            }}
          />
          <button
            onClick={saveTitle}
            disabled={saving}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid rgba(34,197,94,0.3)", background: "rgba(34,197,94,0.08)", cursor: "pointer", fontSize: 13 }}
          >
            ✓
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); cancelEditing(); }}
            style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid rgba(239,68,68,0.3)", background: "rgba(239,68,68,0.08)", cursor: "pointer", fontSize: 13 }}
          >
            ✕
          </button>
        </div>
      ) : (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <p style={{ margin: 0, fontSize: 15, fontWeight: 600, flex: 1 }}>{job.title}</p>
          <button
            onClick={startEditing}
            title="Rename"
            style={{ padding: "3px 6px", borderRadius: 6, border: "1px solid transparent", background: "none", cursor: "pointer", opacity: 0.4, fontSize: 12 }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.opacity = "1"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.opacity = "0.4"; }}
          >
            ✏️
          </button>
        </div>
      )}

      {/* Meta */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-soft)" }}>
        <span style={{ padding: "1px 8px", borderRadius: 20, border: "1px solid var(--line)", fontSize: 11 }}>{job.type}</span>
        <span>·</span>
        <span>{job.createdAt}</span>
      </div>

      {/* Progress bar for processing */}
      {job.status === "processing" && typeof job.progress === "number" && (
        <div>
          <div style={{
            display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--ink-soft)", marginBottom: 6,
          }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#f97316", flexShrink: 0, animation: "pulse 1.5s infinite" }} />
            {job.progressMessage || "Analysis in progress..."}
          </div>
          <div style={{ height: 4, borderRadius: 2, background: "var(--paper-2)", overflow: "hidden" }}>
            <div style={{
              height: "100%", borderRadius: 2,
              background: "linear-gradient(90deg, #f97316, #fbbf24)",
              width: `${job.progress}%`, transition: "width 0.5s ease",
            }} />
          </div>
          <p style={{ margin: "4px 0 0", fontSize: 11, color: "var(--ink-soft)" }}>{job.progress}% complete</p>
        </div>
      )}

      {/* Actions — pushed to bottom */}
      <div style={{ display: "flex", gap: 8, marginTop: "auto" }}>
        <button
          onClick={(e) => { e.stopPropagation(); router.push(`/analyse/jobs/${job.id}`); }}
          style={{
            flex: 1, padding: "7px 0", borderRadius: 8,
            border: "1px solid var(--line)", background: "none",
            cursor: "pointer", fontSize: 13,
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.background = "var(--paper-2)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.background = "none"; }}
        >
          View Details
        </button>
        <button
          onClick={handleDelete}
          title="Delete analysis"
          style={{
            padding: "7px 10px", borderRadius: 8,
            border: "1px solid var(--line)", background: "none",
            cursor: "pointer", fontSize: 13, color: "var(--ink-soft)",
            transition: "all 0.15s",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLElement).style.color = "#ef4444"; (e.currentTarget as HTMLElement).style.borderColor = "rgba(239,68,68,0.3)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLElement).style.color = "var(--ink-soft)"; (e.currentTarget as HTMLElement).style.borderColor = "var(--line)"; }}
        >
          🗑
        </button>
      </div>
    </div>
  );
}

function typeEmoji(type: string): string {
  const map: Record<string, string> = { Spend: "📊", Inventory: "📦", Contracts: "📄", Logistics: "🚛" };
  return map[type] ?? "📈";
}
